import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "gameedge.pwa.installPromptDismissed";
const SHOWN_DELAY_MS = 4500; // give the user a moment to land before nudging

/**
 * InstallPrompt — a mobile-only bottom toast that appears the first time we
 * detect the browser's `beforeinstallprompt` event. Tapping "Install" triggers
 * the native add-to-home-screen flow. Persists the user's dismissal.
 */
export const InstallPrompt = () => {
  const [evt, setEvt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const dismissed = (() => {
      try { return localStorage.getItem(DISMISS_KEY); } catch { return "1"; }
    })();
    if (dismissed) return;

    const handler = (e) => {
      e.preventDefault();
      setEvt(e);
      setTimeout(() => setVisible(true), SHOWN_DELAY_MS);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installed = () => {
      try { localStorage.setItem(DISMISS_KEY, "installed"); } catch { /* ignore */ }
      setVisible(false);
    };
    window.addEventListener("appinstalled", installed);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
  };

  const install = async () => {
    if (!evt) return;
    try {
      await evt.prompt();
      await evt.userChoice;
    } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible || !evt) return null;
  return (
    <div
      className="lg:hidden fixed bottom-24 inset-x-3 z-40 rounded-xl border border-border bg-gray-0 p-3 shadow-card-lg flex items-center gap-3 animate-fade-up"
      data-testid="install-prompt"
    >
      <div className="flex size-9 items-center justify-center rounded-lg bg-secondary/10 text-secondary shrink-0">
        <Download className="size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold text-gray-900">Install GameEdge</div>
        <div className="text-[11px] leading-snug text-gray-500">Add to your home screen for one-tap access.</div>
      </div>
      <button
        onClick={install}
        className="iso-btn-primary !py-1.5 !text-[11px] shrink-0"
        data-testid="install-prompt-cta"
      >
        Install
      </button>
      <button
        onClick={dismiss}
        className="iso-btn-ghost !p-1.5 shrink-0"
        aria-label="Dismiss"
        data-testid="install-prompt-dismiss"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
};
